import React, { useState, useEffect, useContext } from "react";
import { Container, Row, Col } from "react-bootstrap";
import DatePicker from "react-datepicker";
import moment from "moment";
import { FiSearch } from "react-icons/fi";
import PageTitle from "../../components/common/PageTitle";
import SmallStats from "./SmallStats";
import BatchByPeriod from "./BatchByPeriod";
import PieReport from "./PieReport";
import TopReferrals from "./TopReferrals";
import BatchReportService from "../../services/batchReport.service";
import SupplierService from "../../services/supplier.service";
import { AuthContext, setAuthStorage } from "../../contexts/AuthContext";
import "react-datepicker/dist/react-datepicker.css";
import "./Dashboard.scss";

function Dashboard() {
  const { setIsLoading } = useContext(AuthContext);

  const [dateFrom, set_dateFrom] = useState(
    moment().subtract(1, "months").toDate()
  );
  const [dateTo, set_dateTo] = useState(moment().toDate());
  const [smallStats, set_smallStats] = useState([...TopRowStats]);
  const [batches_all, set_batches_all] = useState(0);
  const [batches_pending, set_batches_pending] = useState(0);
  const [batches_completed, set_batches_completed] = useState(0);
  const [batches_failed, set_batches_failed] = useState(0);
  const [suppliers_total, set_suppliers_total] = useState([]);
  const [suppliers_configured, set_suppliers_configured] = useState([]);
  const [month_view, set_month_view] = useState({
    labels: [],
    pending: [],
    failed: [],
    completed: [],
  });
  const [top_suppliers, set_top_suppliers] = useState([]);

  useEffect(() => {
    fetchSuppliers();
  }, []);

  function fetchSuppliers() {
    setIsLoading(true);
    if (suppliers_total.length) {
      setSupplierConfigured(suppliers_total);
      fetchBatchReport(suppliers_total);
    } else {
      let suplist = [];
      SupplierService.getAll()
        .then((response) => {
          if (
            response.data &&
            response.data.data &&
            response.data.data.length
          ) {
            suplist = response.data.data;
          }
          set_suppliers_total(suplist);
          setSupplierConfigured(suplist);
          fetchBatchReport(suplist);
        })
        .catch((error) => {
          setIsLoading(false);
          console.log("fetch suppliers catch => ", error);
        });
    }
  }

  function setSupplierConfigured(supplierlist) {
    const sup_confgrd = supplierlist.reduce((arr, sup) => {
      if (moment(sup.createdAt).isBetween(dateFrom, dateTo, "days", []))
        arr.push(sup);
      return arr;
    }, []);
    set_suppliers_configured(sup_confgrd);
    set_smallStats(
      smallStats.map((smt, inx) => {
        if (inx === 3)
          smt.value = `${sup_confgrd.length}/${supplierlist.length}`;
        return smt;
      })
    );
  }

  function fetchBatchReport(supplierList) {
    BatchReportService.fetchByDates({
      dateFrom,
      dateTo,
      dateField: "updatedAt",
      sortOrder: "asc",
    })
      .then((response) => {
        setIsLoading(false);
        if (response.data && response.data.data && response.data.data.length) {
          categoriseBatchReport(response.data.data, supplierList);
          composeMonthlyOverview(response.data.data);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.log("fetch batch report catch => ", error);
      });
  }

  function categoriseBatchReport(btch_all, suplr_all) {
    const supplierBatchObj = {};
    const btch_pending = [],
      btch_failed = [],
      btch_completed = [];

    for (let i = 0; i < btch_all.length; i++) {
      const btchitem = btch_all[i];
      const sup_code = btchitem.supplierCode;

      supplierBatchObj[sup_code] = supplierBatchObj[sup_code]
        ? ++supplierBatchObj[sup_code]
        : 1;

      switch (btchitem.step) {
        case "downloaded":
        case "validation":
        case "File Process":
          btch_pending.push(btchitem);
          break;
        case "failed":
          btch_failed.push(btchitem);
          break;
        case "process":
        case "complete":
        case "cancelled":
          btch_completed.push(btchitem);
          break;
        default:
          console.info("Default case in segregating batch reports.");
      }
    }

    const top_suplrs = Object.entries(supplierBatchObj)
      .sort(([, valA], [, valB]) => valB - valA)
      .slice(0, 5)
      .map(([sKey, sVal]) => {
        const supInx = suplr_all.findIndex(
          (suplr) => suplr.supplierCode == sKey
        );
        return supInx !== -1
          ? { title: suplr_all[supInx].supplierName, value: sVal }
          : { title: sKey, value: sVal };
      });

    set_top_suppliers(top_suplrs);
    set_batches_all(btch_all);
    set_batches_pending(btch_pending);
    set_batches_failed(btch_failed);
    set_batches_completed(btch_completed);
    set_smallStats([
      { ...smallStats[0], value: btch_all.length },
      {
        ...smallStats[1],
        value: `${btch_failed.length}/${btch_completed.length}`,
      },
      { ...smallStats[2], value: btch_pending.length },
      { ...smallStats[3] },
    ]);
  }

  function composeMonthlyOverview(btch_list) {
    const enddate = moment(dateTo);
    const startdate = moment(dateFrom);
    const dayArr = [],
      pendingArr = [],
      failedArr = [],
      completedArr = [];
    let batchCntr = 0,
      batchItm = undefined;
    while (startdate.isSameOrBefore(enddate)) {
      dayArr.push(startdate.format("DD MMM"));
      pendingArr.push(0);
      failedArr.push(0);
      completedArr.push(0);
      while (batchCntr < btch_list.length) {
        batchItm = btch_list[batchCntr];
        if (moment(batchItm.updatedAt).isSame(startdate, "day")) {
          switch (batchItm.step) {
            case "downloaded":
            case "validation":
            case "File Process":
              pendingArr[pendingArr.length - 1]++;
              break;
            case "failed":
              failedArr[failedArr.length - 1]++;
              break;
            case "process":
            case "complete":
            case "cancelled":
              completedArr[completedArr.length - 1]++;
              break;
            default:
            // console.info("Default case in monthly overview switch.");
          }
          batchCntr++;
        } else {
          break;
        }
      }
      startdate.add(1, "days");
    }
    set_month_view({
      labels: dayArr,
      pending: pendingArr,
      failed: failedArr,
      completed: completedArr,
    });
  }

  function applyFilter() {
    fetchSuppliers();
  }

  return (
    <Container fluid className="main-content-container dashboard px-2">
      {/* Page Header */}
      <Row className="page-header bg-red py-4 dashboard-header">
        <PageTitle
          title="Dashboard"
          subtitle=""
          className="text-sm-left mb-3"
          xs="auto"
          sm="auto"
          md="auto"
        />

        <Col md="7" className="dashboard-date">
          <Row>
            <Col md="5">
              <label className="me-2">From Date</label>
              <DatePicker
                selected={dateFrom}
                onChange={set_dateFrom}
                placeholderText="Select"
                dropdownMode="select"
                dateFormat="dd/MM/yyyy"
                className="form-control"
              />
            </Col>
            <Col md="5">
              <label className="me-2">To Date</label>
              <DatePicker
                selected={dateTo}
                onChange={set_dateTo}
                placeholderText="Select"
                dropdownMode="select"
                dateFormat="dd/MM/yyyy"
                className="form-control"
              />
            </Col>
            <Col md="auto" className="d-flex">
              <button
                className="btn theme-search"
                onClick={applyFilter}
                disabled={!dateFrom || !dateTo}
              >
                <FiSearch size={16} />
              </button>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Small Stats Blocks */}
      <Row>
        {smallStats.map((stats, idx) => (
          <Col
            className="dashboard-smallstats col-lg mb-4"
            key={idx}
            {...stats.attrs}
          >
            <SmallStats
              id={`small-stats-${idx}`}
              variation="1"
              chartData={stats.datasets}
              chartLabels={stats.chartLabels}
              label={stats.label}
              value={stats.value}
              percentage={stats.percentage}
              increase={stats.increase}
              decrease={stats.decrease}
            />
          </Col>
        ))}
      </Row>

      <Row>
        {/* Users Overview */}
        <Col lg="8" md="12" sm="12" className="mb-4">
          <BatchByPeriod
            title={`Batch Overview by Period`}
            labels={month_view.labels}
            pending={month_view.pending}
            failed={month_view.failed}
            completed={month_view.completed}
          />
        </Col>

        {/* Users by Device */}
        <Col lg="4" md="6" sm="12" className="mb-4">
          <PieReport
            title={`Batch Processing Share`}
            completed={batches_completed.length}
            pending={batches_pending.length}
            failed={batches_failed.length}
          />
        </Col>

        {/* Top Referrals */}
        <Col lg="6" md="12" sm="12" className="mb-4">
          <TopReferrals topSuppliers={top_suppliers} />
        </Col>
      </Row>
    </Container>
  );
}

Dashboard.propTypes = {
  /**
   * The small stats dataset.
   */
};

const TopRowStats = [
  {
    label: "Files Received",
    value: "0",
    percentage: "0%",
    increase: false,
    decrease: true,
    chartLabels: [null, null, null, null, null, null, null],
    attrs: { md: "4", sm: "6" },
    datasets: [
      {
        label: "Today",
        fill: "start",
        borderWidth: 1.5,
        backgroundColor: "rgb(0,123,255,0.1)",
        borderColor: "rgb(0,123,255)",
        data: [3, 2, 3, 2, 4, 5, 4],
      },
    ],
  },
  {
    label: "Files Failed / Completed",
    value: "0",
    percentage: "0%",
    increase: false,
    decrease: true,
    chartLabels: [null, null, null, null, null, null, null],
    attrs: { md: "4", sm: "6" },
    datasets: [
      {
        label: "Today",
        fill: "start",
        borderWidth: 1.5,
        backgroundColor: "rgba(255,65,105,0.1)",
        borderColor: "rgb(255,65,105)",
        data: [1, 7, 1, 3, 1, 4, 8],
      },
    ],
  },
  {
    label: "Records Pending",
    value: "0",
    percentage: "0%",
    increase: true,
    chartLabels: [null, null, null, null, null, null, null],
    attrs: { md: "6", sm: "6" },
    datasets: [
      {
        label: "Today",
        fill: "start",
        borderWidth: 1.5,
        backgroundColor: "rgba(23,198,113,0.1)",
        borderColor: "rgb(23,198,113)",
        data: [1, 2, 3, 3, 3, 4, 4],
      },
    ],
  },
  {
    label: "Suppliers Configured",
    value: "0",
    percentage: "0",
    increase: false,
    decrease: true,
    chartLabels: [null, null, null, null, null, null, null],
    attrs: { md: "4", sm: "6" },
    datasets: [
      {
        label: "Today",
        fill: "start",
        borderWidth: 1.5,
        backgroundColor: "rgba(255,180,0,0.1)",
        borderColor: "rgb(255,180,0)",
        data: [2, 3, 3, 3, 4, 3, 3],
      },
    ],
  },
];

export default Dashboard;
